import DashboardLayout from "@/components/DashboardLayout";
import { HlsPlayer } from "@/components/HlsPlayer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, API } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Camera, Loader2, Pause, Play, Radio, RefreshCw, Video } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const REFRESH_INTERVAL_MS = 10000;

function timeAgo(ms) {
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 10) return "acum câteva secunde";
  if (s < 60) return `acum ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `acum ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `acum ${h}h ${m % 60}min`;
  return new Date(ms).toLocaleString("ro-RO");
}

function CloudSnapshotCard({ camera }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [recordedAt, setRecordedAt] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(0);
  const intervalRef = useRef(null);
  const imgSrcRef = useRef(null);

  const fetchSnapshot = useCallback(async () => {
    try {
      const token = localStorage.getItem("hgs_token");
      const resp = await fetch(`${API}/live-snapshot/${camera.id}?t=${Date.now()}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok) {
        if (resp.status === 404) { const d = await resp.json().catch(() => null); setError((d?.detail?.message) || d?.message || "Snapshot indisponibil. Verificați Cloud Sync."); }
        else if (resp.status === 401) setError("Sesiune expirată. Reautentificați-vă.");
        else setError(`Eroare server: ${resp.status}`);
        setLoading(false); return;
      }
      const recorded = resp.headers.get("X-Snapshot-Recorded-At");
      if (recorded) setRecordedAt(parseInt(recorded, 10));
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      if (imgSrcRef.current) URL.revokeObjectURL(imgSrcRef.current);
      imgSrcRef.current = objectUrl;
      setImgSrc(objectUrl); setError(null); setLoading(false); setLastRefresh(Date.now());
    } catch (e) { setError("Eroare de rețea. Reîncercăm..."); setLoading(false); }
  }, [camera.id]);

  useEffect(() => { fetchSnapshot(); }, [fetchSnapshot]);
  useEffect(() => {
    if (paused) { if (intervalRef.current) clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(fetchSnapshot, REFRESH_INTERVAL_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [fetchSnapshot, paused]);
  useEffect(() => () => { if (imgSrcRef.current) URL.revokeObjectURL(imgSrcRef.current); }, []);

  const flipStyle = camera.flipVideo ? { transform: "rotate(180deg)" } : undefined;

  return (
    <Card data-testid={`live-card-${camera.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{camera.name}</CardTitle>
          <div className="flex gap-1">
            <Badge variant="outline" className="text-xs"><Camera className="h-3 w-3 mr-1" />Cloud</Badge>
            {camera.flipVideo && <Badge variant="secondary" className="text-xs">180°</Badge>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {loading && !imgSrc ? (
          <div className="aspect-video rounded-md bg-muted animate-pulse flex items-center justify-center"><RefreshCw className="h-6 w-6 text-muted-foreground animate-spin" /></div>
        ) : error && !imgSrc ? (
          <div className="aspect-video rounded-md bg-muted flex flex-col items-center justify-center text-sm text-muted-foreground p-4 text-center gap-2">
            <AlertTriangle className="h-6 w-6" /><span>{error}</span>
            <span className="text-xs">Configurați Cloud Sync și maparea camerei în <strong>Cloud Sync (Reolink)</strong>.</span>
          </div>
        ) : (
          <div className="relative aspect-video rounded-md bg-black overflow-hidden">
            {imgSrc && <img src={imgSrc} alt={`Live - ${camera.name}`} className="w-full h-full object-contain" style={flipStyle} />}
            <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/60 rounded-full px-2 py-0.5">
              <span className={`h-2 w-2 rounded-full ${paused ? "bg-yellow-400" : "bg-red-500 animate-pulse"}`} />
              <span className="text-[10px] text-white font-medium">{paused ? "PAUZĂ" : "LIVE"}</span>
            </div>
            {error && <div className="absolute bottom-2 left-2 right-2 bg-red-900/80 text-white text-xs rounded px-2 py-1">{error}</div>}
          </div>
        )}
        <div className="flex items-center gap-2 text-xs">
          <Button size="sm" variant="outline" onClick={() => setPaused(!paused)} className="gap-1">{paused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}{paused ? "Redare" : "Pauză"}</Button>
          <Button size="sm" variant="outline" onClick={fetchSnapshot} className="gap-1"><RefreshCw className="h-3 w-3" />Refresh</Button>
          <span className="text-muted-foreground ml-auto text-right">{recordedAt ? <>Ultima detecție: {timeAgo(recordedAt)}</> : lastRefresh ? <>Actualizat: {timeAgo(lastRefresh)}</> : null}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function HlsLiveCard({ camera }) {
  const { data, isLoading } = useQuery({ queryKey: ["stream-url", camera.id], queryFn: () => api.gateway.streamUrl(camera.id) });
  return (
    <Card data-testid={`live-hls-card-${camera.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{camera.name}</CardTitle>
          <div className="flex gap-1">
            <Badge variant="outline" className="text-xs"><Video className="h-3 w-3 mr-1" />HLS</Badge>
            {camera.flipVideo && <Badge variant="secondary" className="text-xs">180°</Badge>}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="aspect-video rounded-md bg-muted animate-pulse flex items-center justify-center"><Loader2 className="h-6 w-6 text-muted-foreground animate-spin" /></div>
        ) : data?.available ? (
          <HlsPlayer src={data.hlsUrl} flip={camera.flipVideo} />
        ) : (
          <div className="aspect-video rounded-md bg-muted flex flex-col items-center justify-center text-sm text-muted-foreground p-4 text-center gap-2">
            <AlertTriangle className="h-6 w-6" /><span>{data?.message || "Flux live indisponibil."}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function LiveViewPage() {
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: () => api.projects.list() });
  const { data: cameras } = useQuery({ queryKey: ["cameras"], queryFn: () => api.cameras.list() });
  const [projectId, setProjectId] = useState("");
  const [mode, setMode] = useState("snapshot");

  const liveCameras = useMemo(() => {
    if (!cameras) return [];
    return cameras.filter((c) => !projectId || c.projectId === Number(projectId));
  }, [cameras, projectId]);

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6" data-testid="live-page">
        <header>
          <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">Live View</h1>
          <p className="text-sm text-muted-foreground mt-1">
            <strong>Snapshot Cloud</strong>: ultimul frame din Reolink Cloud, actualizat automat. <strong>Live HLS</strong>: flux în timp real prin gateway-ul go2rtc (configurați URL-ul în Setări & Gateway).
          </p>
        </header>

        <Card>
          <CardHeader><CardTitle className="text-base">Mod & filtru</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="grid gap-1.5"><Label>Mod vizualizare</Label>
              <Tabs value={mode} onValueChange={setMode}>
                <TabsList className="w-full">
                  <TabsTrigger value="snapshot" className="flex-1" data-testid="mode-snapshot"><Camera className="h-3.5 w-3.5 mr-1" />Snapshot Cloud</TabsTrigger>
                  <TabsTrigger value="hls" className="flex-1" data-testid="mode-hls"><Video className="h-3.5 w-3.5 mr-1" />Live HLS</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="grid gap-1.5"><Label>Proiect</Label>
              <Select value={projectId || "all"} onValueChange={(v) => setProjectId(v === "all" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Toate proiectele" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Toate proiectele</SelectItem>{projects?.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </CardContent>
        </Card>

        {liveCameras.length === 0 ? (
          <Card><CardContent className="py-12 flex flex-col items-center text-center gap-3"><Radio className="h-10 w-10 text-muted-foreground" /><p className="text-sm text-muted-foreground max-w-md">Nicio cameră configurată. Adăugați o cameră și configurați Cloud Sync / gateway pentru a vedea imagini live.</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {liveCameras.map((c) => mode === "hls" ? <HlsLiveCard key={c.id} camera={c} /> : <CloudSnapshotCard key={c.id} camera={c} />)}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
